import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewContainerRef,
  Type,
  Injector,
  ChangeDetectionStrategy,
  computed,
  input,
  signal,
  createComponent,
  EnvironmentInjector,
  inject,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DynamicDialogRef } from './dynamic-dialog-ref';
import type { DynamicDialogConfig as DynamicDialogConfigType } from './dynamic-dialog.types';
import { ButtonComponent } from '../button/button.component';

/**
 * Dynamic Dialog Component - Razor UI Design System
 * Wrapper component for dynamically loaded dialog content
 */
@Component({
  selector: 'ui-dynamic-dialog',
  standalone: true,
  imports: [],
  templateUrl: './dynamic-dialog.component.html',
  styleUrl: './dynamic-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  readonly contentContainer = viewChild.required('contentContainer', { read: ViewContainerRef });

  readonly dialogId = input<string>('');
  readonly contentComponent = input.required<Type<unknown>>();
  readonly config = input<DynamicDialogConfigType>({});
  readonly dialogRef = input.required<DynamicDialogRef>();

  private environmentInjector = inject(EnvironmentInjector);

  visible = signal(true);

  readonly dialogClasses = computed<string>(() => {
    const baseClasses = [
      'bg-bb-bg-secondary',
      'border',
      'border-bb-border',
      'rounded-xl',
      'shadow-2xl',
      'flex',
      'flex-col',
      'overflow-hidden'
    ];

    const cfg = this.config();
    if (cfg.styleClass) {
      baseClasses.push(cfg.styleClass);
    }

    return baseClasses.join(' ');
  });

  readonly positionClasses = computed<string>(() => {
    const position = this.config().position || 'center';
    const positionMap: Record<string, string> = {
      'center': 'items-center justify-center',
      'top': 'items-start justify-center pt-8',
      'bottom': 'items-end justify-center pb-8',
      'left': 'items-center justify-start pl-8',
      'right': 'items-center justify-end pr-8',
      'top-left': 'items-start justify-start pt-8 pl-8',
      'top-right': 'items-start justify-end pt-8 pr-8',
      'bottom-left': 'items-end justify-start pb-8 pl-8',
      'bottom-right': 'items-end justify-end pb-8 pr-8'
    };
    return positionMap[position] || positionMap['center'];
  });

  readonly contentStyle = computed<Record<string, string>>(() =>
    this.config().contentStyle || {}
  );

  readonly contentMaxHeight = computed<string>(() => {
    const cfg = this.config();
    const hasHeader = cfg.showHeader !== false && cfg.header;
    const hasFooter = !!cfg.footer;
    const headerHeight = hasHeader ? '60px' : '0px';
    const footerHeight = hasFooter ? '60px' : '0px';
    return `calc(${cfg.maxHeight || '90vh'} - ${headerHeight} - ${footerHeight})`;
  });

  ngOnInit(): void {
    this.setupKeyboardHandler();
  }

  ngAfterViewInit(): void {
    // Load content after view is initialized to ensure contentContainer is available
    // This is required because contentContainer is inside an @if block
    this.loadContent();
  }

  ngOnDestroy(): void {
    this.removeKeyboardHandler();
  }

  private loadContent(): void {
    const contentComp = this.contentComponent();
    const container = this.contentContainer();
    if (!contentComp || !container) {return;}

    // Create custom injector with dialog ref and config
    const injector = Injector.create({
      providers: [
        { provide: DynamicDialogRef, useValue: this.dialogRef() },
        { provide: DynamicDialogConfig, useValue: this.config() }
      ],
      parent: this.environmentInjector
    });

    // Create and attach the content component
    const componentRef = createComponent(contentComp, {
      environmentInjector: this.environmentInjector,
      elementInjector: injector
    });

    container.insert(componentRef.hostView);
  }

  close(data?: unknown): void {
    this.visible.set(false);
    this.dialogRef().close(data);
  }

  onBackdropClick(): void {
    if (this.config().dismissableMask) {
      this.close();
    }
  }

  private handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.config().closeOnEscape !== false) {
      event.preventDefault();
      this.close();
    }
  };

  // GAP-FE-057 leak-audit: setupKeyboardHandler / removeKeyboardHandler share
  // the same arrow-function reference for handleKeydown; ngOnDestroy →
  // removeKeyboardHandler ensures symmetric cleanup with no leak path.
  private setupKeyboardHandler(): void {
    const win = this.document.defaultView;
    if (win) {
      win.addEventListener('keydown', this.handleKeydown);
    }
  }

  private removeKeyboardHandler(): void {
    const win = this.document.defaultView;
    if (win) {
      win.removeEventListener('keydown', this.handleKeydown);
    }
  }
}

/**
 * DynamicDialogConfig injection token
 * Used by content components to access dialog config/data
 */
export class DynamicDialogConfig<T = any> {
  data?: T;
  header?: string;
  footer?: string;
  width?: string;
  height?: string;
  closable?: boolean;
  // ... other config properties are available on the actual config object
}
